import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// PORT DEFENSE — TOS easter egg, deluxe edition.
// Now with sound, juice, and a story.
//
// Controls: ← → rotate · ↑ thrust · space fire · P pause · ESC close · R restart · M mute
//
// CUSTOMIZATION: Search for `// CUSTOMIZE:` to find the bits you'll want to
// replace with your own company name, employee names, etc.

// ---- tuning ----
const CRANE_SIZE = 16
const CRANE_THRUST = 0.07
const CRANE_TURN = 0.065
const FRICTION = 0.991
const TWISTLOCK_SPEED = 6
const TWISTLOCK_LIFE = 65

const TIER_SPEEDS: Record<1 | 2 | 3, number> = { 3: 0.55, 2: 1.3, 1: 2.1 }
const TIER_RADII: Record<1 | 2 | 3, number> = { 3: 52, 2: 22, 1: 10 }
const TIER_TEU: Record<1 | 2 | 3, number> = { 3: 200, 2: 20, 1: 1 }

interface LineColor {
  name: string
  body: string
}

const LINE_COLORS: LineColor[] = [
  { name: 'MAEK', body: '#3a8fd6' },
  { name: 'HPAG', body: '#d97a2c' },
  { name: 'EVRG', body: '#3aa15a' },
  { name: 'MEDS', body: '#e6c14a' },
  { name: 'ONEY', body: '#d94a8f' },
  { name: 'COSC', body: '#c43838' },
]

const REEFER_COLOR = '#e8e8ee'

// CUSTOMIZE: replace with your company name. Used in the final shift.
const COMPANY_NAME = 'TERMINAL OS'

// CUSTOMIZE: names that occasionally appear as BIC codes during the late
// "glitch" shifts. Replace with your team's names — first name + initial works
// well, kept to 4 chars like a real BIC owner code.
const CREW_NAMES = ['ALEX', 'JESS', 'MARI', 'DEVA', 'KENT', 'RHEA', 'NOOR', 'TOMS']

interface ShiftMessage {
  title: string
  sub: string
  glitch?: boolean
  boss?: boolean
  ending?: boolean
}

// Story beats keyed by shift number. Banners appear between shifts.
const SHIFT_MESSAGES: Record<number, ShiftMessage> = {
  1: { title: 'SHIFT 01', sub: 'berth open · standard operations' },
  4: { title: 'ANOMALY DETECTED', sub: 'vessels not on manifest' },
  7: { title: 'WARNING', sub: 'port authority unresponsive', glitch: true },
  10: {
    title: 'OPERATIONS TERMINATED',
    sub: `${COMPANY_NAME} services discontinued · continuing unauthorized`,
    glitch: true,
  },
  13: { title: 'MANIFEST CORRUPTED', sub: 'BIC codes returning crew names', glitch: true },
  16: {
    title: 'FINAL SHIPMENT INBOUND',
    sub: 'one vessel · slow approach',
    glitch: true,
    boss: true,
  },
  20: { title: 'THANK YOU FOR YOUR SERVICE', sub: 'the port is yours now', ending: true },
}

// ---- helpers ----
function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}
function bicCode(prefix?: string, useCrewName = false): string {
  const owner = useCrewName ? pick(CREW_NAMES) + 'U' : prefix || pick(LINE_COLORS).name + 'U'
  let digits = ''
  for (let i = 0; i < 7; i++) digits += Math.floor(Math.random() * 10)
  return owner + ' ' + digits
}

// ---- AUDIO ENGINE ----
// All sounds synthesized via Web Audio API — no files, no deps.
// Created lazily on first user interaction (browsers block audio until then).
interface ThrustNode {
  src: AudioBufferSourceNode
  gain: GainNode
}

type WindowWithWebkitAudio = Window & {
  webkitAudioContext?: typeof AudioContext
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false
  private thrustNode: ThrustNode | null = null

  init() {
    if (this.ctx) return
    try {
      const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.25
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // audio unavailable — fail silently
    }
  }
  private env(node: GainNode, attack: number, decay: number, peak = 1) {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }
  fire() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(120, this.ctx.currentTime + 0.08)
    this.env(gain, 0.005, 0.08, 0.15)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.1)
  }
  hit(tier: number) {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.15 + tier * 0.15
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 200 + tier * 400
    const gain = this.ctx.createGain()
    this.env(gain, 0.01, dur, 0.5)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
  }
  thrust(active: boolean) {
    if (!this.ctx || !this.masterGain || this.muted) {
      if (this.thrustNode) {
        try {
          this.thrustNode.src.stop()
        } catch {
          // already stopped
        }
        this.thrustNode = null
      }
      return
    }
    if (active && !this.thrustNode) {
      const buf = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate)
      const data = buf.getChannelData(0)
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
      const src = this.ctx.createBufferSource()
      src.buffer = buf
      src.loop = true
      const filter = this.ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = 180
      const gain = this.ctx.createGain()
      gain.gain.value = 0.08
      src.connect(filter).connect(gain).connect(this.masterGain)
      src.start()
      this.thrustNode = { src, gain }
    } else if (!active && this.thrustNode) {
      this.thrustNode.gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.05)
      try {
        this.thrustNode.src.stop(this.ctx.currentTime + 0.06)
      } catch {
        // already stopped
      }
      this.thrustNode = null
    }
  }
  foghorn() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 80
    const t = this.ctx.currentTime
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.4, t + 0.3)
    gain.gain.linearRampToValueAtTime(0.4, t + 0.8)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.4)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(t + 1.5)
  }
  damage() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(220, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(55, this.ctx.currentTime + 0.4)
    this.env(gain, 0.01, 0.4, 0.3)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.45)
  }
  toggleMute() {
    this.muted = !this.muted
    if (this.muted) this.thrust(false)
  }
}

// ---- entity types ----
interface VesselEntity {
  kind: 'vessel'
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  tier: 3
  rotation: number
  rotationSpeed: number
  line: LineColor
  stack: { height: number; color: string }[]
  name: string
  boss: boolean
  bossHP: number
  ghost: boolean
}

interface ContainerEntity {
  kind: 'container'
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  tier: 2
  rotation: number
  rotationSpeed: number
  color: string
  isReefer: boolean
  code: string
}

interface DebrisEntity {
  kind: 'debris'
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  tier: 1
  rotation: number
  rotationSpeed: number
  type: 'pallet' | 'barrel' | 'crate'
}

type Entity = VesselEntity | ContainerEntity | DebrisEntity

interface Twistlock {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  rotation: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

interface Ticker {
  x: number
  y: number
  life: number
  text: string
}

interface Crane {
  x: number
  y: number
  vx: number
  vy: number
  angle: number
  thrusting: boolean
  invincible: number
  muzzleFlash: number
}

type GameStatus = 'playing' | 'paused' | 'gameover' | 'ending'

interface GameState {
  crane: Crane
  twistlocks: Twistlock[]
  entities: Entity[]
  particles: Particle[]
  tickers: Ticker[]
  teu: number
  lives: number
  shift: number
  status: GameStatus
  fireCooldown: number
  time: number
  shake: number
  hitPause: number
  glitchAmount: number
  ending: boolean
}

interface HudState {
  teu: number
  lives: number
  shift: number
  status: GameStatus
  muted: boolean
}

interface VesselOpts {
  boss?: boolean
  ghost?: boolean
}

// ---- entity factories ----
function makeVessel(x: number, y: number, opts: VesselOpts = {}): VesselEntity {
  const angle = rand(0, Math.PI * 2)
  const speed = TIER_SPEEDS[3] * (opts.boss ? 0.4 : 1)
  const line = pick(LINE_COLORS)
  const stackCols = opts.boss ? 14 : 6 + Math.floor(Math.random() * 3)
  const stack: { height: number; color: string }[] = []
  for (let i = 0; i < stackCols; i++) {
    stack.push({ height: 1 + Math.floor(Math.random() * 3), color: pick(LINE_COLORS).body })
  }
  return {
    kind: 'vessel',
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: opts.boss ? 95 : TIER_RADII[3],
    tier: 3,
    rotation: angle,
    rotationSpeed: 0,
    line,
    stack,
    name: opts.boss
      ? `${COMPANY_NAME.replace(/ /g, '_')}-001`
      : opts.ghost
        ? '???-????'
        : line.name + '-' + (1000 + Math.floor(Math.random() * 8999)),
    boss: !!opts.boss,
    bossHP: opts.boss ? 8 : 0,
    ghost: !!opts.ghost,
  }
}
function makeContainer(x: number, y: number, useCrewName = false): ContainerEntity {
  const angle = rand(0, Math.PI * 2)
  const speed = TIER_SPEEDS[2]
  const isReefer = Math.random() < 0.16
  const line = pick(LINE_COLORS)
  return {
    kind: 'container',
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: TIER_RADII[2],
    tier: 2,
    rotation: rand(0, Math.PI * 2),
    rotationSpeed: rand(-0.04, 0.04),
    color: isReefer ? REEFER_COLOR : line.body,
    isReefer,
    code: bicCode(line.name + 'U', useCrewName),
  }
}
function makeDebris(x: number, y: number): DebrisEntity {
  const angle = rand(0, Math.PI * 2)
  const speed = TIER_SPEEDS[1]
  return {
    kind: 'debris',
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: TIER_RADII[1],
    tier: 1,
    rotation: rand(0, Math.PI * 2),
    rotationSpeed: rand(-0.1, 0.1),
    type: pick(['pallet', 'barrel', 'crate'] as const),
  }
}
function spawnShift(level: number, w: number, h: number): Entity[] {
  if (SHIFT_MESSAGES[level]?.boss) {
    return [makeVessel(w / 2, -100, { boss: true })]
  }
  if (SHIFT_MESSAGES[level]?.ending) return []

  const count = 2 + level
  const vessels: Entity[] = []
  for (let i = 0; i < count; i++) {
    const edge = Math.floor(Math.random() * 4)
    let x: number, y: number
    if (edge === 0) {
      x = -60
      y = Math.random() * h
    } else if (edge === 1) {
      x = w + 60
      y = Math.random() * h
    } else if (edge === 2) {
      x = Math.random() * w
      y = -60
    } else {
      x = Math.random() * w
      y = h + 60
    }
    const isGhost = level >= 5 && Math.random() < 0.08 + level * 0.005
    vessels.push(makeVessel(x, y, { ghost: isGhost }))
  }
  return vessels
}

interface PortDefenseProps {
  onClose?: () => void
}

const PortDefense: FC<PortDefenseProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const [hud, setHud] = useState<HudState>({
    teu: 0,
    lives: 3,
    shift: 1,
    status: 'playing',
    muted: false,
  })
  const [banner, setBanner] = useState<ShiftMessage | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const w = () => canvas.width / dpr
    const h = () => canvas.height / dpr

    // CUSTOMIZE: feel free to change this console banner.
    // eslint-disable-next-line no-console
    console.log(
      `%c\n  ╔═══════════════════════════════════════╗\n  ║   THANKS FOR SAILING WITH ${COMPANY_NAME.padEnd(11)}  ║\n  ║   THE PORT IS NOW YOURS.              ║\n  ╚═══════════════════════════════════════╝\n`,
      'color:#ffd166; font-family:ui-monospace, monospace;',
    )

    const showBanner = (msg: ShiftMessage) => {
      setBanner(msg)
      setTimeout(() => setBanner(null), 3500)
    }

    stateRef.current = {
      crane: {
        x: w() / 2,
        y: h() / 2,
        vx: 0,
        vy: 0,
        angle: -Math.PI / 2,
        thrusting: false,
        invincible: 120,
        muzzleFlash: 0,
      },
      twistlocks: [],
      entities: spawnShift(1, w(), h()),
      particles: [],
      tickers: [],
      teu: 0,
      lives: 3,
      shift: 1,
      status: 'playing',
      fireCooldown: 0,
      time: 0,
      shake: 0,
      hitPause: 0,
      glitchAmount: 0,
      ending: false,
    }

    setTimeout(() => showBanner(SHIFT_MESSAGES[1]), 300)

    const onKeyDown = (e: KeyboardEvent) => {
      audio.init()
      keysRef.current[e.key] = true
      if ([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
        e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (e.key === 'p' || e.key === 'P') {
        if (s.status === 'playing') s.status = 'paused'
        else if (s.status === 'paused') s.status = 'playing'
        setHud(p => ({ ...p, status: s.status }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if ((e.key === 'r' || e.key === 'R') && (s.status === 'gameover' || s.status === 'ending')) {
        s.crane = {
          x: w() / 2,
          y: h() / 2,
          vx: 0,
          vy: 0,
          angle: -Math.PI / 2,
          thrusting: false,
          invincible: 120,
          muzzleFlash: 0,
        }
        s.twistlocks = []
        s.particles = []
        s.tickers = []
        s.entities = spawnShift(1, w(), h())
        s.teu = 0
        s.lives = 3
        s.shift = 1
        s.status = 'playing'
        s.glitchAmount = 0
        s.ending = false
        setHud({ teu: 0, lives: 3, shift: 1, status: 'playing', muted: audio.muted })
        setTimeout(() => showBanner(SHIFT_MESSAGES[1]), 300)
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const wrap = (obj: { x: number; y: number }) => {
      if (obj.x < -80) obj.x += w() + 160
      if (obj.x > w() + 80) obj.x -= w() + 160
      if (obj.y < -80) obj.y += h() + 160
      if (obj.y > h() + 80) obj.y -= h() + 160
    }

    const explode = (x: number, y: number, color: string, count = 10) => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const speed = rand(1, 4)
        s.particles.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 40),
          color,
          size: rand(1.5, 3),
        })
      }
    }

    let animationId = 0

    const draw = () => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      const W = w(),
        H = h()
      s.time++

      // Glitch effect ramps with story progression.
      const targetGlitch = s.shift >= 13 ? 0.6 : s.shift >= 10 ? 0.3 : s.shift >= 7 ? 0.1 : 0
      s.glitchAmount += (targetGlitch - s.glitchAmount) * 0.05

      // ---- screen shake ----
      let shakeX = 0,
        shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      // Trail fade.
      ctx.fillStyle = 'rgba(8, 14, 24, 0.32)'
      ctx.fillRect(-20, -20, W + 40, H + 40)

      // ---- background ----
      ctx.strokeStyle = 'rgba(60, 80, 110, 0.12)'
      ctx.lineWidth = 1
      const grid = 60
      for (let x = (s.time * 0.05) % grid; x < W; x += grid) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, H)
        ctx.stroke()
      }
      for (let y = 0; y < H; y += grid) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(W, y)
        ctx.stroke()
      }
      ctx.strokeStyle = 'rgba(80, 140, 180, 0.08)'
      for (let i = 0; i < 6; i++) {
        const y = ((i * 137 + s.time * 0.4) % (H + 100)) - 50
        ctx.beginPath()
        ctx.moveTo(0, y)
        for (let x = 0; x <= W; x += 40) {
          ctx.lineTo(x, y + Math.sin((x + s.time * 2) * 0.02) * 3)
        }
        ctx.stroke()
      }

      // Glitch scanlines
      if (s.glitchAmount > 0.05 && Math.random() < s.glitchAmount) {
        ctx.fillStyle = `rgba(255, 109, 207, ${s.glitchAmount * 0.15})`
        const y = Math.random() * H
        ctx.fillRect(0, y, W, 2 + Math.random() * 4)
      }

      // ---- hit pause: freeze updates but keep drawing ----
      const updating = s.status === 'playing' && s.hitPause === 0
      if (s.hitPause > 0) s.hitPause--

      if (updating) {
        // ---- crane ----
        const c = s.crane
        if (keysRef.current['ArrowLeft']) c.angle -= CRANE_TURN
        if (keysRef.current['ArrowRight']) c.angle += CRANE_TURN
        const wasThrusting = c.thrusting
        c.thrusting = !!keysRef.current['ArrowUp']
        if (c.thrusting !== wasThrusting) audio.thrust(c.thrusting)
        if (c.thrusting) {
          c.vx += Math.cos(c.angle) * CRANE_THRUST
          c.vy += Math.sin(c.angle) * CRANE_THRUST
        }
        c.vx *= FRICTION
        c.vy *= FRICTION
        c.x += c.vx
        c.y += c.vy
        wrap(c)
        if (c.invincible > 0) c.invincible--
        if (c.muzzleFlash > 0) c.muzzleFlash--

        // ---- firing ----
        if (s.fireCooldown > 0) s.fireCooldown--
        if (keysRef.current[' '] && s.fireCooldown === 0 && s.twistlocks.length < 5) {
          s.twistlocks.push({
            x: c.x + Math.cos(c.angle) * CRANE_SIZE,
            y: c.y + Math.sin(c.angle) * CRANE_SIZE,
            vx: Math.cos(c.angle) * TWISTLOCK_SPEED + c.vx,
            vy: Math.sin(c.angle) * TWISTLOCK_SPEED + c.vy,
            life: TWISTLOCK_LIFE,
            rotation: 0,
          })
          s.fireCooldown = 9
          c.muzzleFlash = 4
          audio.fire()
        }

        s.twistlocks = s.twistlocks.filter(t => {
          t.x += t.vx
          t.y += t.vy
          t.rotation += 0.3
          t.life--
          wrap(t)
          return t.life > 0
        })

        s.entities.forEach(e => {
          e.x += e.vx
          e.y += e.vy
          if (e.kind !== 'vessel') e.rotation += e.rotationSpeed
          else e.rotation = Math.atan2(e.vy, e.vx)
          wrap(e)
        })

        // ---- collisions: twistlocks vs entities ----
        for (let i = s.entities.length - 1; i >= 0; i--) {
          const ent = s.entities[i]
          for (let j = s.twistlocks.length - 1; j >= 0; j--) {
            const t = s.twistlocks[j]
            const dx = ent.x - t.x,
              dy = ent.y - t.y
            if (dx * dx + dy * dy < ent.radius * ent.radius) {
              s.twistlocks.splice(j, 1)

              // Boss takes multiple hits before dying.
              if (ent.kind === 'vessel' && ent.boss && ent.bossHP > 1) {
                ent.bossHP--
                explode(t.x, t.y, '#ffd166', 6)
                audio.hit(1)
                s.shake = Math.max(s.shake, 4)
                break
              }

              s.entities.splice(i, 1)
              const isVesselBoss = ent.kind === 'vessel' && ent.boss
              const isVesselGhost = ent.kind === 'vessel' && ent.ghost
              const isReeferContainer = ent.kind === 'container' && ent.isReefer
              const teuMult = isVesselBoss ? 10 : isVesselGhost ? 5 : isReeferContainer ? 2 : 1
              const teuGain = TIER_TEU[ent.tier] * teuMult
              s.teu += teuGain

              const tickerText = isVesselBoss
                ? `+${teuGain} TEU · FAREWELL`
                : isVesselGhost
                  ? `+${teuGain} TEU · GHOST`
                  : `+${teuGain} TEU${isReeferContainer ? ' ❄' : ''}`
              s.tickers.push({ x: ent.x, y: ent.y, life: 60, text: tickerText })

              const color =
                ent.kind === 'vessel'
                  ? ent.line.body
                  : ent.kind === 'container'
                    ? ent.color
                    : '#9a8f6a'
              explode(ent.x, ent.y, color, ent.tier === 3 ? 24 : ent.tier === 2 ? 14 : 8)
              audio.hit(ent.tier)
              s.shake = Math.max(s.shake, ent.tier === 3 ? 12 : ent.tier === 2 ? 6 : 3)
              s.hitPause = ent.tier === 3 ? 4 : ent.tier === 2 ? 2 : 0

              const useCrewNames = s.shift >= 13
              if (ent.kind === 'vessel') {
                const n = ent.boss ? 12 : 4 + Math.floor(Math.random() * 3)
                for (let k = 0; k < n; k++)
                  s.entities.push(makeContainer(ent.x, ent.y, useCrewNames))
                if (ent.boss) {
                  s.shake = 30
                  s.hitPause = 12
                  // After the boss falls, jump to the ending shift.
                  setTimeout(() => {
                    const cs = stateRef.current
                    if (!cs) return
                    cs.shift = 20
                    cs.entities = []
                    cs.status = 'ending'
                    showBanner(SHIFT_MESSAGES[20])
                    setHud(p => ({ ...p, shift: 20, status: 'ending' }))
                    audio.foghorn()
                  }, 1800)
                }
              } else if (ent.kind === 'container') {
                const n = 2 + Math.floor(Math.random() * 2)
                for (let k = 0; k < n; k++) s.entities.push(makeDebris(ent.x, ent.y))
              }
              break
            }
          }
        }

        // ---- collisions: crane vs entities ----
        if (c.invincible === 0) {
          for (const ent of s.entities) {
            const dx = ent.x - c.x,
              dy = ent.y - c.y
            if (dx * dx + dy * dy < (ent.radius + CRANE_SIZE * 0.6) ** 2) {
              s.lives--
              c.x = W / 2
              c.y = H / 2
              c.vx = 0
              c.vy = 0
              c.invincible = 120
              s.shake = 18
              s.hitPause = 6
              explode(c.x, c.y, '#ffd166', 20)
              audio.damage()
              audio.thrust(false)
              if (s.lives <= 0) s.status = 'gameover'
              break
            }
          }
        }

        // ---- particles ----
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vx *= 0.96
          p.vy *= 0.96
          p.life--
          return p.life > 0
        })

        // ---- tickers ----
        s.tickers = s.tickers.filter(tk => {
          tk.y -= 0.6
          tk.life--
          return tk.life > 0
        })

        // ---- next shift ----
        if (s.entities.length === 0) {
          s.shift++
          const msg = SHIFT_MESSAGES[s.shift]
          if (msg) {
            showBanner(msg)
            audio.foghorn()
          }
          if (msg?.ending) {
            s.status = 'ending'
          } else {
            s.entities = spawnShift(s.shift, W, H)
            c.invincible = 90
          }
        }

        setHud(prev => {
          if (
            prev.teu === s.teu &&
            prev.lives === s.lives &&
            prev.shift === s.shift &&
            prev.status === s.status
          )
            return prev
          return { ...prev, teu: s.teu, lives: s.lives, shift: s.shift, status: s.status }
        })
      }

      // ============ DRAWING ============

      // ---- entities ----
      s.entities.forEach(e => {
        ctx.save()
        ctx.translate(e.x, e.y)
        ctx.rotate(e.rotation)

        if (e.kind === 'vessel') {
          const len = e.boss ? 200 : 110
          const beam = e.boss ? 50 : 28

          if (e.ghost) {
            ctx.setLineDash([4, 4])
            ctx.strokeStyle = 'rgba(255, 109, 207, 0.7)'
            ctx.lineWidth = 1.2
          } else {
            ctx.fillStyle = e.boss ? '#0e1420' : '#1a1f2e'
            ctx.strokeStyle = e.boss ? '#ffd166' : e.line.body
            ctx.lineWidth = e.boss ? 2 : 1.5
          }
          ctx.beginPath()
          ctx.moveTo(-len / 2, -beam / 2)
          ctx.lineTo(len / 2 - 14, -beam / 2)
          ctx.lineTo(len / 2, 0)
          ctx.lineTo(len / 2 - 14, beam / 2)
          ctx.lineTo(-len / 2, beam / 2)
          ctx.closePath()
          if (!e.ghost) ctx.fill()
          ctx.stroke()
          ctx.setLineDash([])

          if (!e.ghost) {
            ctx.fillStyle = '#2a3142'
            ctx.fillRect(-len / 2 + 6, -beam / 2 + 4, e.boss ? 24 : 14, beam - 8)
            const stackStart = -len / 2 + (e.boss ? 36 : 24)
            const stackEnd = len / 2 - (e.boss ? 36 : 24)
            const stackW = (stackEnd - stackStart) / e.stack.length
            e.stack.forEach((col, i) => {
              ctx.fillStyle = col.color
              ctx.fillRect(stackStart + i * stackW + 1, -beam / 2 + 6, stackW - 2, beam - 12)
            })
          }
          ctx.restore()

          ctx.fillStyle = e.boss
            ? '#ffd166'
            : e.ghost
              ? 'rgba(255, 109, 207, 0.8)'
              : 'rgba(232, 228, 255, 0.5)'
          ctx.font = e.boss
            ? 'bold 11px ui-monospace, Menlo, monospace'
            : '9px ui-monospace, Menlo, monospace'
          ctx.textAlign = 'center'
          ctx.fillText(e.name, e.x, e.y + e.radius + 14)

          if (e.boss) {
            for (let i = 0; i < 8; i++) {
              ctx.fillStyle = i < e.bossHP ? '#ffd166' : 'rgba(255, 209, 102, 0.2)'
              ctx.fillRect(e.x - 40 + i * 11, e.y - e.radius - 18, 8, 4)
            }
          }
          return
        }

        if (e.kind === 'container') {
          const cw = 38,
            ch = 18
          ctx.fillStyle = e.color
          ctx.strokeStyle = 'rgba(0,0,0,0.4)'
          ctx.lineWidth = 1
          ctx.fillRect(-cw / 2, -ch / 2, cw, ch)
          ctx.strokeRect(-cw / 2, -ch / 2, cw, ch)
          ctx.strokeStyle = 'rgba(0,0,0,0.18)'
          for (let i = 1; i < 6; i++) {
            const x = -cw / 2 + (cw / 6) * i
            ctx.beginPath()
            ctx.moveTo(x, -ch / 2 + 2)
            ctx.lineTo(x, ch / 2 - 2)
            ctx.stroke()
          }
          if (e.isReefer) {
            ctx.fillStyle = '#3aa15a'
            ctx.fillRect(cw / 2 - 4, -2, 2, 4)
          }
          ctx.fillStyle = 'rgba(0,0,0,0.55)'
          ctx.font = '6px ui-monospace, Menlo, monospace'
          ctx.textAlign = 'center'
          ctx.fillText(e.code, 0, 1)
        }

        if (e.kind === 'debris') {
          ctx.strokeStyle = '#9a8f6a'
          ctx.lineWidth = 1.2
          if (e.type === 'pallet') {
            ctx.strokeRect(-8, -5, 16, 10)
            ctx.beginPath()
            ctx.moveTo(-8, 0)
            ctx.lineTo(8, 0)
            ctx.stroke()
          } else if (e.type === 'barrel') {
            ctx.beginPath()
            ctx.arc(0, 0, 7, 0, Math.PI * 2)
            ctx.stroke()
            ctx.beginPath()
            ctx.moveTo(-7, -2)
            ctx.lineTo(7, -2)
            ctx.moveTo(-7, 2)
            ctx.lineTo(7, 2)
            ctx.stroke()
          } else {
            ctx.strokeRect(-7, -7, 14, 14)
            ctx.beginPath()
            ctx.moveTo(-7, -7)
            ctx.lineTo(7, 7)
            ctx.moveTo(7, -7)
            ctx.lineTo(-7, 7)
            ctx.stroke()
          }
        }

        ctx.restore()
      })

      // ---- particles ----
      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      // ---- twistlocks ----
      s.twistlocks.forEach(t => {
        ctx.save()
        ctx.translate(t.x, t.y)
        ctx.rotate(t.rotation)
        ctx.fillStyle = '#ffd166'
        ctx.fillRect(-2.5, -2.5, 5, 5)
        ctx.restore()
      })

      // ---- crane ----
      const c = s.crane
      const visible = c.invincible === 0 || Math.floor(c.invincible / 6) % 2 === 0
      if (visible && s.status !== 'gameover' && s.status !== 'ending') {
        ctx.save()
        ctx.translate(c.x, c.y)
        ctx.rotate(c.angle)
        ctx.strokeStyle = '#ffd166'
        ctx.lineWidth = 1.6
        ctx.beginPath()
        ctx.moveTo(CRANE_SIZE, -8)
        ctx.lineTo(CRANE_SIZE, 8)
        ctx.stroke()
        ctx.strokeStyle = 'rgba(255, 209, 102, 0.5)'
        ctx.beginPath()
        ctx.moveTo(CRANE_SIZE, -6)
        ctx.lineTo(-CRANE_SIZE * 0.6, -CRANE_SIZE * 0.7)
        ctx.moveTo(CRANE_SIZE, 6)
        ctx.lineTo(-CRANE_SIZE * 0.6, CRANE_SIZE * 0.7)
        ctx.moveTo(CRANE_SIZE, -6)
        ctx.lineTo(CRANE_SIZE * 0.4, -CRANE_SIZE * 0.5)
        ctx.moveTo(CRANE_SIZE, 6)
        ctx.lineTo(CRANE_SIZE * 0.4, CRANE_SIZE * 0.5)
        ctx.stroke()
        ctx.strokeStyle = '#ffd166'
        ctx.strokeRect(-CRANE_SIZE * 0.6, -CRANE_SIZE * 0.7, CRANE_SIZE, CRANE_SIZE * 1.4)
        ctx.fillStyle = '#ffd166'
        ctx.fillRect(-CRANE_SIZE * 0.6 - 1, -CRANE_SIZE * 0.7 - 1, 2, 2)
        ctx.fillRect(CRANE_SIZE * 0.4 - 1, -CRANE_SIZE * 0.7 - 1, 2, 2)
        ctx.fillRect(-CRANE_SIZE * 0.6 - 1, CRANE_SIZE * 0.7 - 1, 2, 2)
        ctx.fillRect(CRANE_SIZE * 0.4 - 1, CRANE_SIZE * 0.7 - 1, 2, 2)

        // Muzzle flash
        if (c.muzzleFlash > 0) {
          ctx.fillStyle = `rgba(255, 240, 180, ${c.muzzleFlash / 4})`
          ctx.beginPath()
          ctx.arc(CRANE_SIZE + 4, 0, 5, 0, Math.PI * 2)
          ctx.fill()
        }

        if (c.thrusting && Math.random() > 0.25) {
          ctx.strokeStyle = 'rgba(120, 200, 230, 0.6)'
          ctx.beginPath()
          ctx.moveTo(-CRANE_SIZE * 0.7, -3)
          ctx.lineTo(-CRANE_SIZE * (1.2 + Math.random() * 0.5), 0)
          ctx.lineTo(-CRANE_SIZE * 0.7, 3)
          ctx.stroke()
        }
        ctx.restore()
      }

      // ---- floating tickers ----
      s.tickers.forEach(tk => {
        const alpha = Math.min(1, tk.life / 30)
        ctx.fillStyle = `rgba(255, 209, 102, ${alpha})`
        ctx.font = '11px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(tk.text, tk.x, tk.y)
      })

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      audio.thrust(false)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      {/* Inline keyframes for the banner fade-in */}
      <style>{`
        @keyframes portDefenseBannerIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>TEU DESTROYED</span>
          <span style={styles.hudValue}>{String(hud.teu).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SHIFT</span>
          <span style={styles.hudValue}>{String(hud.shift).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>CRANES</span>
          <span style={styles.hudValue}>{'▣ '.repeat(Math.max(0, hud.lives))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BERTH</span>
          <span
            style={{ ...styles.hudValue, color: hud.status === 'playing' ? '#3aa15a' : '#d97a2c' }}
          >
            {hud.status === 'playing'
              ? '● ACTIVE'
              : hud.status === 'paused'
                ? '● HOLD'
                : hud.status === 'ending'
                  ? '● HOME'
                  : '● CLOSED'}
          </span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {banner && (
        <div style={{ ...styles.banner, ...(banner.glitch ? styles.bannerGlitch : {}) }}>
          <div style={styles.bannerTitle}>{banner.title}</div>
          {banner.sub && <div style={styles.bannerSub}>{banner.sub}</div>}
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>BERTH OCCUPIED</div>
          <div style={styles.overlaySub}>awaiting pilot — press P to resume</div>
        </div>
      )}

      {hud.status === 'ending' && (
        <div style={styles.overlay}>
          <div style={{ ...styles.overlayTitle, color: '#ffd166' }}>THANK YOU FOR SAILING</div>
          <div style={styles.overlaySub}>
            {COMPANY_NAME} · {new Date().getFullYear()}
          </div>
          <pre style={styles.edi}>
            {`the port is yours now.
${hud.teu.toLocaleString()} TEU passed through.
no shift lasts forever.

— with love, the crew`}
          </pre>
          <div style={styles.overlaySub}>press R to sail again · ESC to close</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT TERMINATED</div>
          <div style={styles.overlaySub}>
            final manifest: {hud.teu} TEU across {hud.shift} shift{hud.shift !== 1 ? 's' : ''}
          </div>
          <pre style={styles.edi}>
            {`UNB+UNOA:1+TOS:01+OPERATOR:01+${new Date().toISOString().slice(0, 10).replace(/-/g, '')}+0001'
BGM+99+CLOSURE+9'
DTM+137:${new Date().toISOString().slice(11, 16).replace(':', '')}:201'
FTX+AAI+++THANKS_FOR_SAILING_WITH_${COMPANY_NAME.replace(/ /g, '_')}'
UNT+5+0001'`}
          </pre>
          <div style={styles.overlaySub}>press R to restart · ESC to close</div>
        </div>
      )}

      <div style={styles.help}>
        ← → rotate &nbsp;·&nbsp; ↑ thrust &nbsp;·&nbsp; space fire &nbsp;·&nbsp; P hold
        &nbsp;·&nbsp; M mute
      </div>
    </div>
  )
}

export default PortDefense

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 24,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  close: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  banner: {
    position: 'absolute',
    top: '32%',
    left: '50%',
    transform: 'translateX(-50%)',
    textAlign: 'center',
    pointerEvents: 'none',
    zIndex: 4,
    animation: 'portDefenseBannerIn 0.5s ease-out',
  },
  bannerGlitch: {
    textShadow: '2px 0 #ff6dcf, -2px 0 #3a8fd6',
  },
  bannerTitle: {
    fontSize: 32,
    letterSpacing: '0.4em',
    color: '#ffd166',
    fontWeight: 'bold',
  },
  bannerSub: {
    marginTop: 8,
    fontSize: 11,
    letterSpacing: '0.25em',
    color: '#7a8398',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
  },
  overlayTitle: {
    fontSize: 42,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  edi: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
  },
  help: {
    position: 'absolute',
    bottom: 14,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#3a4458',
  },
}
